import Close from '@mdi/svg/svg/close.svg?react';
import classNames from 'classnames';
import React, { memo } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { COLOR, FONT_SIZE, SIZE } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { Button } from '../Button';
import { MDIIcon } from '../MDIIcon';
import { type PillProps } from './Pill.types';

const Pill: React.FC<PillProps> = memo(
  ({
    className = '',
    dataTestId = 'pill',
    onClick,
    style = EMPTY_OBJ,
    title,
    type = 'light',
    value,
    hasCloseButton = false,
  }) => {
    const legendClass = classNames('pill', type, className, {
      clickable: onClick && !hasCloseButton,
      'has-close-button': hasCloseButton,
    });
    return (
      <Wrapper
        className={legendClass}
        onClick={hasCloseButton ? () => null : onClick}
        onKeyDown={a11yKey(onClick)}
        style={style}
        tabIndex={onClick ? 0 : -1}
        title={title}
        data-testid={`${dataTestId}-${value}`}
      >
        <span>{value as React.ReactNode}</span>
        {hasCloseButton && (
          <Button
            className="close-button"
            dataTestId={`${dataTestId}-close`}
            icon={<MDIIcon icon={Close} size={16} />}
            iconOnly={true}
            onClick={onClick}
            type="clear"
          />
        )}
      </Wrapper>
    );
  },
);

// Styled Components
const pillHeight = 26;
const pillPadding = 1;
const pillFullSize = pillHeight + 2 * pillPadding;

const Wrapper = styled.div`
  background: ${COLOR['light-gray-light']};
  border: 1px solid ${COLOR['light-gray-darkest']};
  border-radius: ${pillFullSize + 'px'};
  color: ${COLOR['dark-gray-light']};
  display: inline-block;
  font-family: inherit;
  font-size: ${FONT_SIZE['13']};
  font-style: normal;
  font-weight: 400;
  line-height: ${pillHeight + 'px'};
  margin-left: ${SIZE['12']};
  padding: ${pillPadding + 'px'} ${SIZE['12']};
  position: relative;
  text-align: center;
  text-rendering: geometricPrecision;
  width: auto;

  &:focus {
    outline: none;
  }

  &.has-close-button {
    padding-right: 34px;
  }

  &.light {
    background: ${COLOR['white']};
    border-color: ${COLOR['light-gray']};
  }

  &.dark {
    background: ${COLOR['dark-gray-dark']};
    border-color: ${COLOR['dark-gray-light']};
    color: ${COLOR['white']};

    button.styled.clear.mode-dark {
      &:hover,
      &:focus {
        color: ${COLOR['white']};
      }
    }
  }

  &.clickable {
    cursor: pointer;

    &:focus,
    &:hover {
      background: ${COLOR['primary']};
      border-color: ${COLOR['primary']};
      color: ${COLOR['white']};
    }
  }

  &:first-child {
    margin-left: 0;
  }

  &.success,
  &.error,
  &.warning {
    color: ${COLOR['white']};
    font-weight: 500;
  }

  &.success {
    background: ${COLOR['green-dark']};
  }

  &.error {
    background: ${COLOR['red-dark']};
  }

  &.warning {
    background: ${COLOR['yellow-dark']};
  }

  .close-button {
    display: inline-block;
    margin: 0 !important;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    svg {
      margin-top: 4px;
    }
  }
`;

// Display Name
Pill.displayName = 'Pill';

export default Pill;
